import type { ChatToolCall } from '../api/domains/chatbot.api'
import { announceLibraryChange } from '../library/library-refresh'

/**
 * Assistant tools that change geocoding sessions on the server. When a finished turn carries a
 * successful one, the /geocoding pages re-read: `apps/console` has no query cache to invalidate,
 * so this module-level signal stands in for it (the same shape as `library-refresh.ts`).
 */
export const GEOCODING_WRITE_TOOLS: ReadonlySet<string> = new Set([
  'prepare_geocoding',
  'run_geocoding',
  'resume_geocoding',
  'stop_geocoding',
  'export_geocoding_layer',
  'regeocode_weak_rows',
  'delete_geocoding_session',
])

export interface GeocodingChange {
  /** Sessions that changed or were created (a re-geocode's new pass included). */
  sessionIds: string[]
  /** Sessions that no longer exist. */
  deletedIds: string[]
}

type Listener = (change: GeocodingChange) => void

const listeners = new Set<Listener>()

export function subscribeGeocodingRefresh(listener: Listener): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

interface WriteResult {
  success?: unknown
  error?: unknown
  saved?: unknown
  session_id?: unknown
  new_session_id?: unknown
  layer_id?: unknown
}

const idOf = (v: unknown): string | null => (typeof v === 'string' && v ? v : null)

const hasFileId = (input: unknown): boolean =>
  !!input && typeof input === 'object' && typeof (input as { file_id?: unknown }).file_id === 'string'

/**
 * A result counts only when `success === true`. A run still going is a success (`still_running`),
 * so it announces too and the page shows the session as processing.
 */
export function announceGeocodingWrites(toolCalls: readonly ChatToolCall[]): void {
  const sessionIds: string[] = []
  const deletedIds: string[] = []
  const layerIds: string[] = []

  for (const call of toolCalls) {
    if (!GEOCODING_WRITE_TOOLS.has(call.name)) continue
    const result = call.result as WriteResult | null

    if (!result || typeof result !== 'object') continue
    // A run that failed to launch changed the session too (it is now `failed`).
    const failedRun = result.success === false && result.error === 'run_failed'
    if (result.success !== true && !failedRun) continue
    // prepare_geocoding without a mapping on an existing session only reads; re-reading the
    // wizard then would throw away the user's unsaved edits for nothing.
    if (call.name === 'prepare_geocoding' && !result.saved && !hasFileId(call.input)) continue

    const id = idOf(result.session_id)

    if (call.name === 'delete_geocoding_session') {
      if (id) deletedIds.push(id)
      continue
    }

    if (id) sessionIds.push(id)

    const next = idOf(result.new_session_id)
    if (next) sessionIds.push(next)

    // The exported layer is a new Library item.
    const layer = call.name === 'export_geocoding_layer' ? idOf(result.layer_id) : null
    if (layer) layerIds.push(layer)
  }

  if (layerIds.length > 0) announceLibraryChange({ changedIds: layerIds, deletedIds: [] })
  if (sessionIds.length === 0 && deletedIds.length === 0) return

  for (const listener of [...listeners]) {
    try {
      listener({ sessionIds, deletedIds })
    } catch {
      // One page's failed refresh must not stop the others.
    }
  }
}

/** True when a change names this session, as changed or as deleted. */
export function changeTouches(change: GeocodingChange, sessionId: string | null | undefined): boolean {
  if (!sessionId) return false

  return change.sessionIds.includes(sessionId) || change.deletedIds.includes(sessionId)
}
